import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import QueryString from "qs";

export type FiltersValues = {
  name: string;
  value: boolean | string | null | string[];
  type: string;
  fixed?: boolean;
};

export interface TransactionFilterState {
  filters: {
    minAmount: FiltersValues;
    maxAmount: FiltersValues;
    startDate: FiltersValues;
    endDate: FiltersValues;
    hasComments: FiltersValues;
    lastMonth?: FiltersValues;
    uncategorised: FiltersValues;
    intercompany?: FiltersValues;
    fromPlaid: FiltersValues;
    fromCSV: FiltersValues;
    manuallyAddedTxn: FiltersValues;
    manualEntry?: FiltersValues;
    cashFlow?: FiltersValues;
    categoryIds?: FiltersValues;
    vendors?: FiltersValues;
    fromIds?: FiltersValues;
    bankStatement?: FiltersValues;
    hasNoVendors?: FiltersValues;
    splitTransactions?: FiltersValues;
    notReviewed?: FiltersValues;
    reviewed?: FiltersValues;
    hasInvoiceOrMemo?: FiltersValues;
    excludedTransactions?: FiltersValues;
    bankTransactions?: FiltersValues;
    classification?: FiltersValues;
    classes?: FiltersValues;
    noClassification?: FiltersValues;
  };
}

export type FilterName =
  | "minAmount"
  | "maxAmount"
  | "startDate"
  | "endDate"
  | "hasComments"
  | "lastMonth"
  | "uncategorised"
  | "intercompany"
  | "fromPlaid"
  | "fromCSV"
  | "manuallyAddedTxn"
  | "manualEntry"
  | "cashFlow"
  | "categoryIds"
  | "vendors"
  | "fromIds"
  | "bankStatement"
  | "hasNoVendors"
  | "splitTransactions"
  | "excludedTransactions"
  | "notReviewed"
  | "reviewed"
  | "hasInvoiceOrMemo"
  | "bankTransactions"
  | "classification"
  | "classes"
  | "noClassification";

const initialState: TransactionFilterState = {
  filters: {
    cashFlow: {
      name: "Cash Flow",
      value: null,
      type: "amount",
    },
    minAmount: { name: "Min Amount", value: null, type: "amount" },
    maxAmount: { name: "Max Amount", value: null, type: "amount" },

    startDate: { name: "Since", value: null, type: "transactionDate" },
    endDate: { name: "Till", value: null, type: "transactionDate" },

    hasComments: { name: "Has Comments", value: false, type: "others" },
    fromPlaid: { name: "Bank connection", value: false, type: "sources" },
    fromCSV: { name: "CSV", value: false, type: "sources" },
    manuallyAddedTxn: {
      name: "Manually added",
      value: false,
      type: "sources",
    },
    uncategorised: {
      name: "Uncategorised",
      value: false,
      type: "others",
      fixed: true,
    },
    categoryIds: {
      name: "Categories",
      value: null,
      type: "category",
      fixed: false,
    },
    vendors: {
      name: "Vendors",
      value: null,
      type: "vendors",
      fixed: false,
    },
    fromIds: {
      name: "Source",
      value: null,
      type: "from",
      fixed: false,
    },
    manualEntry: {
      name: "Manual Entry",
      value: false,
      type: "from",
      fixed: false,
    },
    bankStatement: {
      name: "Bank statement",
      value: false,
      type: "from",
      fixed: false,
    },
    reviewed: {
      name: "Reviewed",
      value: false,
      type: "others",
      fixed: true,
    },
    notReviewed: {
      name: "Not Reviewed",
      value: false,
      type: "others",
      fixed: true,
    },
    hasNoVendors: {
      name: "Unknown Vendors",
      value: false,
      type: "others",
      fixed: true,
    },
    splitTransactions: {
      name: "Split Transactions",
      value: false,
      type: "others",
      fixed: false,
    },
    hasInvoiceOrMemo: {
      name: "Has Invoice or Memo",
      value: false,
      type: "others",
      fixed: false,
    },
    excludedTransactions: {
      name: "Excluded Transactions",
      value: false,
      type: "others",
      fixed: false,
    },
    bankTransactions: {
      name: "Bank Transactions",
      value: null,
      type: "others",
      fixed: false,
    },
    classification: {
      name: "Classification",
      value: null,
      type: "classification",
      fixed: false,
    },
    classes: {
      name: "Classes",
      value: null,
      type: "classes",
      fixed: false,
    },
    noClassification: {
      name: "No Classification",
      value: null,
      type: "noClassification",
      fixed: false,
    },
  },
};

export const transactionFilter = createSlice({
  name: "transactionFilter",
  initialState,
  reducers: {
    setTxnFilters: (state, action: PayloadAction<QueryString.ParsedQs>) => {
      const payload = action.payload;

      const updatedValues = Object.keys(payload).reduce((acc, el) => {
        const value = payload[el] === "true" ? true : payload[el];
        const newState = {
          ...initialState.filters?.[el as FilterName],
          value,
        };

        return { ...acc, [el]: { ...newState } };
      }, {});

      state.filters = {
        ...state.filters,
        ...updatedValues,
      };
    },
    resetTxnFilters: (state) => {
      state.filters = initialState.filters;
    },
    restoreFilters: (
      state,
      action: PayloadAction<TransactionFilterState["filters"]>
    ) => {
      state.filters = action.payload;
    },
  },
});

export const { setTxnFilters, resetTxnFilters, restoreFilters } =
  transactionFilter.actions;

export default transactionFilter.reducer;
